import { t } from "@lingui/macro"
import { useCallback } from "react"
import { Stack, useTheme } from "@mui/material"
import { observer } from "mobx-react"

import { AccessGroup } from "src/components/ModalAccessGroup/AccessGroup"
import { FormFields } from "src/modals/content-items/components/formFields"
import { useStore } from "src/store/lib/useStore"
import { content_ContentItemEntity } from "src/api"
import { AutoCompleteTextField } from "src/components/AutoCompleteTextField"
import { GlobalStore } from "src/store"
import { ContentItemsStore } from "src/views/content-items/store"
import { ContentItemsModalStore } from "src/modals/content-items/store"
import { ConfigMode } from "src/types/dynamic-configurable-form-fields/config-mode"
import { useGetContentTypes } from "src/views/content-items/hooks/useGetContentTypes"

export const SettingsSection = observer(() => {
    const { palette } = useTheme()
    const globalStore = useStore(GlobalStore)
    const contentItemsStore = useStore(ContentItemsModalStore)
    const contentTypes = useGetContentTypes()

    const handleSelectOnChange = useCallback(
        (_, newValue) => {
            contentItemsStore.setConfigType(newValue)
        },
        [contentItemsStore],
    )

    const isOptionEqualToValue = useCallback((option, value) => {
        if (option === null || value === null) return false
        return option.id === value.id
    }, [])

    return (
        <Stack spacing={2}>
            <AccessGroup
                accessGroupId={contentItemsStore.accessGroupId as number}
                errorMessage={
                    contentItemsStore.form.error("accessGroupID") ?? ""
                }
                disabled={contentItemsStore.isEditMode}
            />
            <FormFields.ContentItemsTextInput
                label={t`navigation-items-view.setting-section.internal-name`}
                name="internalName"
                rowLength={1}
                disabled={contentItemsStore.isReadOnly}
                maxLength={255}
                value={contentItemsStore.form.get("internalName")}
                helperText={contentItemsStore.form.error("internalName") ?? ""}
                handleTextInputChange={(event) => {
                    contentItemsStore.form.set(
                        "internalName",
                        event.target.value,
                    )
                }}
            />
            <FormFields.ContentItemsDropdown
                optionArray={contentTypes}
                selectedValue={contentItemsStore.form.get("entity")}
                onSelectionChange={(value) => {
                    contentItemsStore.form.set(
                        "entity",
                        `${value as content_ContentItemEntity}`,
                    )
                    contentItemsStore.form.set("entityID", 0)
                    void contentItemsStore.featureTargetList(
                        contentItemsStore.accessGroupId,
                    )
                }}
                errorMessage={contentItemsStore.form.error("entity") ?? ""}
                disabled={contentItemsStore.isReadOnly}
                label={t`navigation-items-view.setting-section.content-type`}
            />
            {contentItemsStore.form.get("entity") !== undefined && (
                <FormFields.ContentItemsDropdown
                    optionArray={contentItemsStore.featureTargetLists}
                    selectedValue={contentItemsStore.form.get("entityID")}
                    onSelectionChange={(value) =>
                        contentItemsStore.form.set("entityID", Number(value))
                    }
                    errorMessage={
                        Boolean(contentItemsStore.form.get("entityID"))
                            ? contentItemsStore.form.error("entityID") ?? ""
                            : t`errors.required`
                    }
                    disabled={contentItemsStore.isReadOnly}
                    label={t`navigation-items-view.setting-section.target-feature`}
                />
            )}
            <AutoCompleteTextField
                disableCloseOnSelect
                multiple={contentItemsStore.configMode === ConfigMode.Multiple}
                options={contentItemsStore.configTypeOptions}
                value={contentItemsStore.configTypeValue}
                placeholder={t`content-items-modal-location-filter-placeholder`}
                onChange={handleSelectOnChange}
                isOptionEqualToValue={isOptionEqualToValue}
                getOptionLabel={(option) => option?.label ?? ""}
                loading={
                    globalStore.loading.is(
                        ContentItemsStore.LoadingKeys.init,
                    ) ||
                    globalStore.loading.is(
                        ContentItemsStore.LoadingKeys.loading,
                    )
                }
                sx={{
                    backgroundColor: palette.common.white,
                }}
                error={Boolean(contentItemsStore.form.error("config_type"))}
                helperText={contentItemsStore.form.error("config_type") ?? ""}
            />
        </Stack>
    )
})
